import type {
  CreateLabAnalyteMappingBody,
  LabAnalyteMapping,
  LabAnalyteMappingList,
  LabPortalenQuarantineItem,
  LabPortalenStatus,
  LabPortalenSyncNowResult,
  RerunLabPortalenQuarantineResult,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, withCredentials } from "./api";
import { commonHeaders, jsonOr401 } from "./clinician-api";

// TES-442 — the LabPortalen staff routes. Doctor and admin read; only a
// doctor attaches or dismisses (the API's @Roles("doctor") on both writes).
// Both writes carry a reason, which the API audits.

const BASE = `${API_BASE}/employee-api/v1/labportalen`;

export async function fetchLabPortalenStatus(): Promise<LabPortalenStatus> {
  const res = await apiFetch(`${BASE}/status`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// TES-1265 — "Hämta nu": one sweep now instead of waiting for the cron.
// Doctor and admin (a named TES-1040 exception); audited by the API.
export async function syncLabPortalenNow(): Promise<LabPortalenSyncNowResult> {
  const res = await apiFetch(`${BASE}/sync`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify({}),
    ...withCredentials,
  });
  return jsonOr401(res);
}

/** The open quarantine, oldest first (at most 200). */
export async function fetchLabPortalenQuarantine(): Promise<
  LabPortalenQuarantineItem[]
> {
  const res = await apiFetch(`${BASE}/quarantine`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function attachLabPortalenQuarantine(
  id: string,
  labOrderId: string,
  reason: string,
): Promise<{ id: string; status: "attached"; labOrderId: string }> {
  const res = await apiFetch(
    `${BASE}/quarantine/${encodeURIComponent(id)}/attach`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ labOrderId, reason: reason.trim() }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1263 — analyte mappings and the quarantine re-run. Doctor and admin.

export async function fetchAnalyteMappings(): Promise<LabAnalyteMappingList> {
  const res = await apiFetch(`${BASE}/analyte-mappings`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function createAnalyteMapping(
  body: CreateLabAnalyteMappingBody,
): Promise<LabAnalyteMapping> {
  const res = await apiFetch(`${BASE}/analyte-mappings`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function deactivateAnalyteMapping(
  id: string,
  reason: string,
): Promise<LabAnalyteMapping> {
  const res = await apiFetch(
    `${BASE}/analyte-mappings/${encodeURIComponent(id)}/deactivate`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ reason: reason.trim() }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export async function rerunLabPortalenQuarantine(
  reason: string,
): Promise<RerunLabPortalenQuarantineResult> {
  const res = await apiFetch(`${BASE}/quarantine/rerun`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify({ reason: reason.trim() }),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export async function dismissLabPortalenQuarantine(
  id: string,
  reason: string,
): Promise<{ id: string; status: "dismissed" }> {
  const res = await apiFetch(
    `${BASE}/quarantine/${encodeURIComponent(id)}/dismiss`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify({ reason: reason.trim() }),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}
